import {
  type ID,
  type NodeItem,
  type SectionItem,
  selectNodeChildren,
  selectRootNodesBySection,
  selectSectionsByTab,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import type { StructureNode, StructureSection } from '../types'

type RelationsState = Pick<AppState, 'relations'>

const buildNode = (
  id: ID,
  state: RelationsState,
  titleById: Map<ID, string>
): StructureNode => ({
  id,
  title: titleById.get(id) ?? '',
  children: selectNodeChildren(id)(state).map((childId) =>
    buildNode(childId, state, titleById)
  ),
})

// The tree mirrors the canvas of the active tab: same sections in the same
// order, same child order (both come from the relations store, which is what
// the canvas renders from).
//
// Which sections belong to the tab is asked of the relations too, exactly as
// Sections.tsx asks it. The section's own tabId is not a second source of the
// same truth: duplicating a tab copies the field along with the section while
// only the relations are re-parented, so on the copy it still names the tab
// the section came from (OCT-5189).
//
// Deliberately built from the raw slices instead of a selector that assembles
// objects, so a sitemap with hundreds of pages is not rebuilt on every
// unrelated dispatch.
export const useStructureTree = (): StructureSection[] => {
  const activeTabId = useSelector(
    migrationProjectSelector.getActiveOrFirstTabId
  )

  const nodes = useSelector((state: AppState) => state.manager.project.nodes)

  // The raw slice, not getProjectSectionsByTabId: that selector filters and
  // sorts, so it hands back a new array on every call and the memo below would
  // never hold.
  const allSections = useSelector(
    (state: AppState) => state.manager.project.sections
  )

  const relations = useSelector((state: AppState) => state.relations)

  return useMemo(() => {
    const titleById = new Map<ID, string>(
      nodes.map((node: NodeItem) => [node.id, node.title])
    )

    const state: RelationsState = { relations }

    const tabSectionIds = new Set<ID>(selectSectionsByTab(activeTabId)(state))

    const sections = allSections
      .filter((section: SectionItem) => tabSectionIds.has(section.id))
      .sort((a: SectionItem, b: SectionItem) => a.order - b.order)

    return sections.map((section: SectionItem) => ({
      id: section.id,
      title: section.title,
      roots: selectRootNodesBySection(section.id)(state).map((nodeId) =>
        buildNode(nodeId, state, titleById)
      ),
    }))
  }, [nodes, allSections, relations, activeTabId])
}
